<template>
  <Modal v-bind="$attrs" v-on="$listeners" :xIconDisabled="true">
    <div v-if="loading" class="loading-mask">
      Wating...
    </div>
    <div class="main overflow-hidden rounded">
      <div class="overflow-auto">

        <div class="font-bold text-indigo-700 mb-3">
          AKBSに登録完了しました。<br>
          {{ staffName }} さんの {{ savedName }} について、ご確認・操作をお願いします。
        </div>

        <!-- 種類ごとに分けて出す（引き継ぎ・かぶり・作業終了後・退職後の代勤） -->
        <WorkingCheckCard
          v-for="g in groups"
          :key="g.key"
          :list="g.rows"
          :caption="g.caption"
          :radioName="'check' + g.key"
          :staffName="staffName"
        />

        <div class="mt-2 mb-1 mx-2">
          <PrimaryButton text="上記の操作で登録" class="w-full" :disabled="loading" @click="save"></PrimaryButton>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from '@components/Modal.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WorkingCheckCard, { groupByKind } from './WorkingCheckCard.vue'
import * as backend from '@libs/backend'
import * as utils from '@libs/utils'

export default {
  components: {
    Modal,
    PrimaryButton,
    WorkingCheckCard
  },

  props: {
    // 開いているか
    open: {
      type: Boolean,
      default: false
    },
    // 入力画面の情報（req_akbs の1行。id を使う）
    val: {
      type: Object,
      default: () => ({})
    },
    // メインタスク（requestNo を使う）
    rp: {
      type: Object,
      default: () => ({})
    },
    // 清掃員名
    staffName: {
      type: String,
      default: ''
    },
    // 申請の種別名（確認メインタスクの題に使う）
    typeName: {
      type: String,
      default: ''
    },
    // バックが返した確認一覧 { savedName, list }
    workingCheckList: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      // ローディング
      loading: false,
      // 確認一覧（操作の初期値を入れた複製）
      list: [],
      // 一覧に何が入っているか（休み登録・代勤登録・振替登録）
      savedName: ''
    }
  },

  computed: {
    /**
     * 種類ごとに分けた一覧
     */
    groups() {
      return groupByKind(this.list)
    }
  },

  watch: {
    open() {
      if (this.open) {
        this.getData()
      }
    }
  },

  methods: {
    /**
     * 一覧を複製し、操作の初期値を入れる
     * バックは「何もしない」で控えているので、ここで勧める操作に置き換える
     */
    getData() {
      const src = this.workingCheckList || {}
      this.savedName = src.savedName || ''
      const list = utils.clone(src.list || [])

      for (const row of list) {
        // 後から足すとリアクティブにならないため、ここでキーを揃える
        row.insertStatus = null

        // 振替で振替先の日に出しているもの（振替元が変更日・終了日より前）は、引き継げない・削除できない（WorkingCheckCard の buttons と同じ）ので「何もしない」
        const exchangeOnly = !!row.displayDay && String(row.displayDay) !== String(row.day)

        switch (row.kind) {
          // 引き継ぎ：引き継ぐ（代勤も。時間指定でない物件があり、実働時間が同じなら良いため）
          case 'takeover':
            row.action = exchangeOnly ? 'none' : 'move'
            break

          // 作業終了後：削除する
          case 'end':
            row.action = exchangeOnly ? 'none' : 'delete'
            break

          // 退職後：削除する
          case 'retire':
            row.action = 'delete'
            break

          // かぶり：確定している代勤（●：代勤OK・休み取り消し・スポット作業）と時間帯がかぶる日は、今回の物件に休み登録
          //        （確定した代勤はもう更新しないので、かぶりに誰も気づかない。ここで防ぐ。ユーザー決定）
          //        確定していない代勤（登録済みなど。前日に ●：代勤OK へ更新するときに気づける）だけの日と、休み登録だけの日は確認メインタスク
          //        勤務変更していない曜日と、短時間の休みも確認メインタスク
          default:
            row.action = 'insert'
            row.insertStatus = 0
            if (!(row.op || []).some(d => d.confirmed) || row.changeWeek === false || (row.holi || []).some(h => String(h.status || '').includes('短時間'))) {
              row.action = 'task'
              row.insertStatus = null
            }
            break
        }
      }

      this.list = list
    },

    /**
     * 申請の作成者（申請・一時保存を最後に押した人。req_akbs の creator）と、その所属部署
     * 確認メインタスクの依頼先（担当者・部署）にする。所属は主となる部署（main）を store の所属データから引く
     * @returns {Object|null} { id, name, departmentName, departmentId }。作成者が分からなければ null（バックは操作した人にする）
     */
    creator() {
      const creatorId = this.val.creatorId
      if (!creatorId) {
        return null
      }
      const list = this.$store.getters.userDepartment || []
      const ud = list.find(u => u.userId == creatorId && u.main) || list.find(u => u.userId == creatorId)
      const dep = ud ? (this.$store.getters.departmentList || []).find(d => d.departmentId == ud.departmentId) : null
      return {
        id: creatorId,
        name: this.val.creator || (ud ? ud.userName : ''),
        departmentName: dep ? dep.name : '',
        departmentId: ud ? ud.departmentId : ''
      }
    },

    /**
     * 保存（選んだ操作をバックで実行し、req_akbs に控える）
     */
    async save() {
      try {
        this.loading = true

        // 操作した人（承認者）。確認メインタスクの作成者になる
        const ui = this.$store.getters.loginUserData
        const user = {
          id: this.$store.getters.user.id,
          name: this.$store.getters.user.user_name,
          mail: this.$store.getters.user.e_mail_adress,
          departmentName: ui.departmentName,
          departmentId: ui.departmentIdUser
        }

        const res = await backend.postData('akbsInputForm/saveCheckData', {
          id: this.val.id,
          list: this.list,
          savedName: this.savedName,
          typeName: this.typeName,
          requestNo: this.rp.requestNo,
          staffName: this.staffName,
          // 清掃員コード（確認メインタスクから AKBS の稼働管理を開くリンクに使う）
          staffCode: this.val.staffDailyCleaning,
          user,
          creator: this.creator()
        })

        // バックが返す控え（操作した日時・人入り）。返らなければ画面の値で作る
        const saved = res && res.data && res.data.list ? res.data : { savedName: this.savedName, list: this.list }
        this.$emit('saved', saved)
      } catch (error) {
        console.log(error)
        alert('保存に失敗しました。再度お願いします。')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* 幅は中身（表）に合わせる。日が多いときは最大で画面幅いっぱいまで広げ、それ以上は表が横スクロールになる
   （最大は Modal の余白 mx-4 + p-4 分を引いた幅） */
.main {
  width: max-content;
  min-width: 700px;
  max-width: calc(100vw - 6rem);
}
</style>
